import moment from "moment-timezone";
import type { IndexableDocument } from "@/types/search/IndexableDocument";
import { getDateTokens } from "@/helpers/dateFormat";

export const UNDATED_DATE_KEY = "undated";

export interface DayGroup {
	dateKey: string;
	dateLabel: string;
	items: IndexableDocument[];
}

/**
 * Groups media items by their created date (local timezone).
 * Returns groups sorted by date descending (newest first).
 * Items without a valid date are collected into an "Undated" group at the end.
 */
export function groupMediaByDate(items: IndexableDocument[], dateFormat?: string | null): DayGroup[] {
	if (!items || items.length === 0) {
		return [];
	}

	const dateLabelToken = getDateTokens(dateFormat).weekdayFullMonthDayOrdinal;
	const tz = moment.tz.guess();
	const groupMap = new Map<string, DayGroup>();
	const undatedItems: IndexableDocument[] = [];

	for (const item of items) {
		if (!item.created) {
			undatedItems.push(item);
			continue;
		}

		const m = moment.tz(item.created, tz);
		if (!m.isValid()) {
			undatedItems.push(item);
			continue;
		}

		const dateKey = m.format("YYYY-MM-DD");

		if (!groupMap.has(dateKey)) {
			groupMap.set(dateKey, {
				dateKey,
				dateLabel: m.format(dateLabelToken),
				items: []
			});
		}

		groupMap.get(dateKey)!.items.push(item);
	}

	const groups = Array.from(groupMap.values());
	groups.sort((a, b) => b.dateKey.localeCompare(a.dateKey));

	if (undatedItems.length > 0) {
		groups.push({
			dateKey: UNDATED_DATE_KEY,
			dateLabel: "Undated",
			items: undatedItems
		});
	}

	return groups;
}

/**
 * Returns a date key string in MM-DD-YYYY format suitable for zip folder names.
 */
export function dateKeyToFolderName(dateKey: string): string {
	const parts = dateKey.split("-");
	if (parts.length !== 3) {
		return dateKey;
	}
	return `${parts[1]}-${parts[2]}-${parts[0]}`;
}
